/**
 * Deterministic adapter for D&D Beyond character-service v5 payloads.
 *
 * The character-service response is deeply nested and is not a canonical VCS
 * character. Treating it as generic JSON turns definitions into strings such as
 * "[object Object]" and drops secondary classes. This adapter owns that source
 * boundary and emits the same SmartPaste character shape used by the rest of VCS.
 *
 * date_of_change: 2026-08-24
 * rule_reference: D&D Beyond character-service v5 fields; VCS canonical character
 * import contract; D&D 5e multiclass level and proficiency rules.
 */

export const DND_BEYOND_SOURCE_FORMAT = 'dnd-beyond-character-service-v5';

const ABILITIES = [
  { id: 1, name: 'strength', short: 'str' },
  { id: 2, name: 'dexterity', short: 'dex' },
  { id: 3, name: 'constitution', short: 'con' },
  { id: 4, name: 'intelligence', short: 'int' },
  { id: 5, name: 'wisdom', short: 'wis' },
  { id: 6, name: 'charisma', short: 'cha' }
];

const ABILITY_BY_ID = Object.fromEntries(ABILITIES.map((ability) => [ability.id, ability]));
const SKILLS = [
  { key: 'acrobatics', name: 'Acrobatics', ability: 'dexterity' },
  { key: 'animal-handling', name: 'Animal Handling', ability: 'wisdom' },
  { key: 'arcana', name: 'Arcana', ability: 'intelligence' },
  { key: 'athletics', name: 'Athletics', ability: 'strength' },
  { key: 'deception', name: 'Deception', ability: 'charisma' },
  { key: 'history', name: 'History', ability: 'intelligence' },
  { key: 'insight', name: 'Insight', ability: 'wisdom' },
  { key: 'intimidation', name: 'Intimidation', ability: 'charisma' },
  { key: 'investigation', name: 'Investigation', ability: 'intelligence' },
  { key: 'medicine', name: 'Medicine', ability: 'wisdom' },
  { key: 'nature', name: 'Nature', ability: 'intelligence' },
  { key: 'perception', name: 'Perception', ability: 'wisdom' },
  { key: 'performance', name: 'Performance', ability: 'charisma' },
  { key: 'persuasion', name: 'Persuasion', ability: 'charisma' },
  { key: 'religion', name: 'Religion', ability: 'intelligence' },
  { key: 'sleight-of-hand', name: 'Sleight of Hand', ability: 'dexterity' },
  { key: 'stealth', name: 'Stealth', ability: 'dexterity' },
  { key: 'survival', name: 'Survival', ability: 'wisdom' }
];

const SKILL_BY_KEY = Object.fromEntries(SKILLS.map((skill) => [skill.key, skill]));
const DAMAGE_TYPES = new Set([
  'acid', 'bludgeoning', 'cold', 'fire', 'force', 'lightning', 'necrotic',
  'piercing', 'poison', 'psychic', 'radiant', 'slashing', 'thunder'
]);
const CONDITION_NAMES = new Set([
  'blinded', 'charmed', 'deafened', 'frightened', 'grappled', 'incapacitated',
  'invisible', 'paralyzed', 'petrified', 'poisoned', 'prone', 'restrained',
  'stunned', 'unconscious', 'exhausted'
]);
const ALIGNMENTS = {
  1: 'Lawful Good',
  2: 'Neutral Good',
  3: 'Chaotic Good',
  4: 'Lawful Neutral',
  5: 'Neutral',
  6: 'Chaotic Neutral',
  7: 'Lawful Evil',
  8: 'Neutral Evil',
  9: 'Chaotic Evil'
};
const COIN_KEYS = {
  cp: 'copper',
  sp: 'silver',
  ep: 'electrum',
  gp: 'gold',
  pp: 'platinum'
};

const isObject = (value) => Boolean(value && typeof value === 'object' && !Array.isArray(value));

const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toInteger = (value) => {
  const parsed = toNumber(value);
  return parsed === null ? null : Math.trunc(parsed);
};

const normalizeKey = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[’']/g, '')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

const titleCase = (value) => String(value || '')
  .replace(/[-_]+/g, ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase())
  .trim();

const dedupeStrings = (values) => {
  const seen = new Set();
  return (Array.isArray(values) ? values : [])
    .map((value) => String(value || '').trim())
    .filter(Boolean)
    .filter((value) => {
      const key = normalizeKey(value);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const dedupeEntries = (entries) => {
  const seen = new Set();
  return (Array.isArray(entries) ? entries : []).filter((entry) => {
    const key = entry?.sourceId !== null && entry?.sourceId !== undefined
      ? `source:${entry.sourceId}`
      : [
          normalizeKey(entry?.name),
          normalizeKey(entry?.source),
          normalizeKey(entry?.activationType || entry?.actionType),
          String(entry?.level ?? '')
        ].join('|');
    if (!entry?.name || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const parserMeta = (sourceSection) => ({
  sourceParser: 'DndBeyondCharacterAdapter',
  sourceSection,
  parseConfidence: 1,
  knowledgeSource: 'provided',
  knowledgeNote: 'Read directly from the pasted D&D Beyond character export.'
});

const flattenBuckets = (buckets) => {
  if (Array.isArray(buckets)) return [...buckets];
  if (!isObject(buckets)) return [];
  return Object.entries(buckets).flatMap(([bucket, entries]) => (
    (Array.isArray(entries) ? entries : []).map((entry) => ({
      ...entry,
      _ddbBucket: bucket
    }))
  ));
};

const findStatValue = (stats, id) => {
  const entry = (Array.isArray(stats) ? stats : []).find((candidate) => Number(candidate?.id) === Number(id));
  return toNumber(entry?.value);
};

const isGrantedModifier = (modifier) => modifier?.isGranted !== false && modifier?.isGranted !== 0;

const getModifierSubtype = (modifier) => normalizeKey(
  modifier?.subType || modifier?.friendlySubtypeName || modifier?.name
);

const buildAbilities = (character, modifiers) => Object.fromEntries(ABILITIES.map((ability) => {
  const base = findStatValue(character.stats, ability.id) ?? 10;
  const manualBonus = findStatValue(character.bonusStats, ability.id) ?? 0;
  const override = findStatValue(character.overrideStats, ability.id);
  const scoreSubtype = `${ability.name}-score`;
  const scoreModifiers = modifiers.filter((modifier) => (
    isGrantedModifier(modifier)
    && getModifierSubtype(modifier) === scoreSubtype
  ));
  const setValues = scoreModifiers
    .filter((modifier) => normalizeKey(modifier.type) === 'set')
    .map((modifier) => toNumber(modifier.value))
    .filter((value) => value !== null);
  const additiveBonus = scoreModifiers
    .filter((modifier) => ['bonus', 'increase'].includes(normalizeKey(modifier.type)))
    .reduce((sum, modifier) => sum + (toNumber(modifier.value) ?? 0), 0);
  const setValue = setValues.length ? Math.max(...setValues) : null;
  const resolved = override ?? setValue ?? (base + manualBonus + additiveBonus);
  return [ability.name, resolved];
}));

const abilityModifier = (score) => Math.floor(((toNumber(score) ?? 10) - 10) / 2);

const buildClasses = (character) => (Array.isArray(character.classes) ? character.classes : [])
  .map((classEntry) => {
    const definition = isObject(classEntry?.definition) ? classEntry.definition : {};
    const subclass = isObject(classEntry?.subclassDefinition) ? classEntry.subclassDefinition : {};
    const name = String(definition.name || classEntry?.name || '').trim();
    const level = toInteger(classEntry?.level) ?? 0;
    const hitDie = toInteger(definition.hitDice ?? classEntry?.hitDie ?? classEntry?.hitDice);
    if (!name || level <= 0) return null;
    return {
      name,
      level,
      ...(subclass.name ? { subclass: subclass.name } : {}),
      ...(hitDie ? { hitDie: `d${hitDie}`, hitDice: `${level}d${hitDie}` } : {}),
      hitDiceSpent: toInteger(classEntry?.hitDiceUsed) ?? 0,
      isStartingClass: classEntry?.isStartingClass === true
    };
  })
  .filter(Boolean)
  .sort((left, right) => Number(right.isStartingClass) - Number(left.isStartingClass));

const getOverrideValue = (overrides, key, aliases = []) => {
  if (!overrides) return null;
  const keys = [key, ...aliases].map(normalizeKey);
  if (isObject(overrides)) {
    for (const [candidateKey, value] of Object.entries(overrides)) {
      if (keys.includes(normalizeKey(candidateKey))) {
        if (isObject(value)) return toNumber(value.value ?? value.modifier ?? value.bonus);
        return toNumber(value);
      }
    }
  }
  if (Array.isArray(overrides)) {
    const entry = overrides.find((candidate) => keys.includes(normalizeKey(
      candidate?.name || candidate?.subType || candidate?.friendlySubtypeName || candidate?.id
    )));
    return toNumber(entry?.value ?? entry?.modifier ?? entry?.bonus);
  }
  return null;
};

const buildTraining = (character, modifiers, abilities, proficiencyBonus) => {
  const granted = modifiers.filter(isGrantedModifier);
  const hasModifier = (types, subtype) => granted.some((modifier) => (
    types.includes(normalizeKey(modifier.type)) && getModifierSubtype(modifier) === subtype
  ));
  const modifierBonus = (types, subtype) => granted
    .filter((modifier) => types.includes(normalizeKey(modifier.type)) && getModifierSubtype(modifier) === subtype)
    .reduce((sum, modifier) => sum + (toNumber(modifier.value) ?? 0), 0);

  const skillsDetailed = SKILLS.map((skill) => {
    const proficient = hasModifier(['proficiency', 'expertise'], skill.key);
    const expertise = hasModifier(['expertise'], skill.key);
    const halfProficient = hasModifier(['half-proficiency', 'half-proficiency-round-up'], skill.key);
    const explicit = getOverrideValue(character.skillOverrides, skill.key, [skill.name]);
    const base = abilityModifier(abilities[skill.ability]);
    const trainingBonus = expertise
      ? proficiencyBonus * 2
      : (proficient ? proficiencyBonus : (halfProficient ? Math.floor(proficiencyBonus / 2) : 0));
    const situationalBonus = modifierBonus(['bonus'], skill.key);
    return {
      name: skill.name,
      proficient: proficient || expertise,
      expertise,
      bonus: explicit ?? (base + trainingBonus + situationalBonus)
    };
  }).filter((skill) => (
    skill.proficient
    || getOverrideValue(character.skillOverrides, normalizeKey(skill.name), [skill.name]) !== null
  ));

  const savingThrows = {};
  const savingThrowProficiencies = [];
  ABILITIES.forEach((ability) => {
    const subtype = `${ability.name}-saving-throws`;
    const proficient = hasModifier(['proficiency', 'expertise'], subtype);
    const explicit = getOverrideValue(character.savingThrowOverrides, ability.name, [ability.short, subtype]);
    const situationalBonus = modifierBonus(['bonus'], subtype);
    savingThrows[ability.name] = explicit ?? (
      abilityModifier(abilities[ability.name])
      + (proficient ? proficiencyBonus : 0)
      + situationalBonus
    );
    if (proficient) savingThrowProficiencies.push(ability.name);
  });

  const armor = [];
  const weapons = [];
  const tools = [];
  const languages = [];
  granted.forEach((modifier) => {
    const type = normalizeKey(modifier.type);
    const subtype = getModifierSubtype(modifier);
    const label = String(
      modifier.friendlySubtypeName || modifier.name || titleCase(modifier.subType)
    ).trim();
    if (!label) return;

    if (type === 'language' || type === 'language-proficiency') {
      languages.push(label.replace(/\s+Language$/i, ''));
      return;
    }
    if (!['proficiency', 'expertise'].includes(type)) return;
    if (SKILL_BY_KEY[subtype] || subtype.endsWith('-saving-throws')) return;
    if (/armor|shield/.test(subtype)) {
      armor.push(label);
    } else if (/weapon|sword|bow|crossbow|dagger|axe|mace|hammer|spear|staff|club|flail|glaive|halberd|lance|pike|rapier|scimitar|sling|trident|whip/.test(subtype)) {
      weapons.push(label);
    } else {
      tools.push(label);
    }
  });

  return {
    skills: skillsDetailed.map((skill) => skill.name),
    skillsDetailed,
    savingThrows,
    proficiencies: {
      armor: dedupeStrings(armor),
      weapons: dedupeStrings(weapons),
      tools: dedupeStrings(tools),
      savingThrows: dedupeStrings(savingThrowProficiencies),
      languages: dedupeStrings(languages)
    },
    toolProficiencies: dedupeStrings(tools),
    languages: dedupeStrings(languages)
  };
};

const getCharacterValue = (character, typeId, notePattern) => {
  const entry = (Array.isArray(character.characterValues) ? character.characterValues : []).find((candidate) => (
    Number(candidate?.typeId) === Number(typeId)
    || (notePattern && notePattern.test(String(candidate?.notes || '')))
  ));
  return toNumber(entry?.value);
};

const buildSpeed = (character, modifiers) => {
  const explicitWalk = getCharacterValue(character, 2, /walk|speed|movement/i);
  const normal = character?.race?.weightSpeeds?.normal;
  const override = character?.race?.weightSpeeds?.override;
  const source = isObject(override) && Object.values(override).some((value) => (toNumber(value) ?? 0) > 0)
    ? override
    : (isObject(normal) ? normal : null);
  const result = {};
  if (source) {
    ['walk', 'fly', 'swim', 'climb', 'burrow'].forEach((mode) => {
      const value = toNumber(source[mode]);
      if (value !== null && value > 0) result[mode] = value;
    });
  }
  if (explicitWalk !== null) result.walk = explicitWalk;
  if (!result.walk) result.walk = toNumber(character.baseSpeed) ?? 30;

  const hasExplicitOverride = explicitWalk !== null || (isObject(override) && (toNumber(override.walk) ?? 0) > 0);
  if (!hasExplicitOverride) {
    const speedBonus = modifiers
      .filter((modifier) => (
        isGrantedModifier(modifier)
        && normalizeKey(modifier.type) === 'bonus'
        && /(^|-)speed$|walking-speed|unarmored-movement/.test(getModifierSubtype(modifier))
      ))
      .reduce((sum, modifier) => sum + (toNumber(modifier.value) ?? 0), 0);
    result.walk += speedBonus;
  }
  return result;
};

const getItemDefinition = (item) => (isObject(item?.definition) ? item.definition : (isObject(item) ? item : {}));

const getItemType = (definition) => String(
  definition.type
  || definition.filterType
  || definition.baseType
  || (Array.isArray(definition.tags) ? definition.tags[0] : '')
  || 'item'
).trim();

const formatDamageFormula = (dice, fixed) => {
  const diceText = String(dice || '').trim();
  const fixedValue = toNumber(fixed);
  if (!diceText) return fixedValue !== null ? String(fixedValue) : '';
  if (fixedValue === null || fixedValue === 0) return diceText;
  return `${diceText}${fixedValue > 0 ? '+' : ''}${fixedValue}`;
};

const getNamedValue = (value) => (
  isObject(value) ? (value.name || value.label || value.type || '') : value
);

const getStableSourceId = (...values) => values.find((value) => (
  value !== null
  && value !== undefined
  && value !== ''
  && String(value) !== '0'
));

const mapInventoryEntry = (item, sourceSection = 'equipment') => {
  const definition = getItemDefinition(item);
  const customAttack = isObject(item?.customAttack) ? item.customAttack : {};
  const name = String(definition.name || item?.name || '').trim();
  if (!name) return null;
  const properties = dedupeStrings([
    ...(Array.isArray(definition.properties) ? definition.properties.map((property) => property?.name || property) : []),
    ...(Array.isArray(customAttack.properties) ? customAttack.properties : [])
  ]);
  const damageDice = customAttack.damageDice
    || definition.damage?.diceString
    || (definition.damage?.diceCount && definition.damage?.diceValue
      ? `${definition.damage.diceCount}d${definition.damage.diceValue}`
      : '');
  const fixedDamage = customAttack.fixedDamage ?? definition.fixedDamage ?? definition.damage?.fixedValue;
  const damageFormula = formatDamageFormula(damageDice, fixedDamage);
  const normalRange = toNumber(customAttack.range ?? definition.range);
  const longRange = toNumber(customAttack.longRange ?? definition.longRange);
  const attackBonus = toNumber(
    customAttack.attackBonusOverride
    ?? item?.attackBonusOverride
    ?? definition.attackBonus
  );
  const type = getItemType(definition);

  return {
    name,
    type,
    quantity: toNumber(item?.quantity) ?? 1,
    description: String(definition.description || item?.description || '').trim(),
    notes: String(item?.notes || '').trim(),
    weight: toNumber(definition.weight ?? item?.weight),
    rarity: definition.rarity || null,
    magical: definition.magic === true,
    equipped: item?.equipped === true,
    attuned: item?.isAttuned === true || item?.attuned === true,
    requiresAttunement: definition.canAttune === true,
    armorClass: toNumber(definition.armorClass),
    properties,
    ...(damageFormula ? { damage: damageFormula, damageFormula } : {}),
    ...(definition.damageType || customAttack.damageType
      ? { damageType: getNamedValue(definition.damageType || customAttack.damageType) }
      : {}),
    ...(attackBonus !== null ? { attackBonus, toHit: attackBonus } : {}),
    ...(definition.attackType || customAttack.attackType
      ? { attackType: definition.attackType || customAttack.attackType }
      : {}),
    ...(normalRange !== null
      ? { range: { normal: normalRange, ...(longRange !== null ? { long: longRange } : {}) } }
      : {}),
    ...(Array.isArray(customAttack.additionalDamage) && customAttack.additionalDamage.length
      ? { additionalDamage: customAttack.additionalDamage.map((entry) => ({ ...entry })) }
      : {}),
    sourceId: getStableSourceId(definition.id, item?.id) ?? null,
    parserMeta: parserMeta(sourceSection)
  };
};

const buildEquipment = (character) => {
  const inventoryEntries = (Array.isArray(character.inventory) ? character.inventory : [])
    .map((entry) => mapInventoryEntry(entry, 'equipment'))
    .filter(Boolean);
  const customEntries = (Array.isArray(character.customItems) ? character.customItems : [])
    .map((entry) => mapInventoryEntry({ ...entry, definition: entry }, 'equipment'))
    .filter(Boolean);
  // Keep separate inventory rows unless D&D Beyond supplied the same stable item id.
  // Two identical weapons can have different equipped/attuned/container state.
  const seenSourceIds = new Set();
  const allEntries = [...inventoryEntries, ...customEntries].filter((entry) => {
    if (entry?.sourceId === null || entry?.sourceId === undefined) return true;
    const key = String(entry.sourceId);
    if (seenSourceIds.has(key)) return false;
    seenSourceIds.add(key);
    return true;
  });
  const equipment = {
    weapons: [],
    armor: [],
    items: [],
    valuables: [],
    currency: {}
  };
  allEntries.forEach((entry) => {
    const type = normalizeKey(entry.type);
    if (/weapon/.test(type)) equipment.weapons.push(entry);
    else if (/armor|shield/.test(type)) equipment.armor.push(entry);
    else if (/valuable|gem|jewel|art-object/.test(type)) equipment.valuables.push(entry);
    else equipment.items.push(entry);
  });
  Object.entries(isObject(character.currencies) ? character.currencies : {}).forEach(([coin, value]) => {
    const numeric = toNumber(value);
    if (numeric !== null && COIN_KEYS[normalizeKey(coin)]) {
      equipment.currency[COIN_KEYS[normalizeKey(coin)]] = numeric;
    }
  });
  return equipment;
};

const buildArmorClass = (character, equipment, abilities, modifiers) => {
  const directOverride = toNumber(character.overrideArmorClass)
    ?? getCharacterValue(character, 1, /armor\s*class|\bac\b/i);
  if (directOverride !== null) {
    return { value: directOverride, notes: 'D&D Beyond override' };
  }

  const dexterityModifier = abilityModifier(abilities.dexterity);
  const equippedArmor = [...(equipment.armor || [])].filter((entry) => entry.equipped);
  const shields = equippedArmor.filter((entry) => /shield/.test(normalizeKey(entry.type)));
  const bodyArmor = equippedArmor.filter((entry) => !/shield/.test(normalizeKey(entry.type)));
  const shieldBonus = shields.reduce((sum, entry) => sum + (toNumber(entry.armorClass) ?? 0), 0);
  const armorCandidates = bodyArmor.map((entry) => {
    const base = toNumber(entry.armorClass);
    if (base === null) return null;
    const type = normalizeKey(entry.type);
    if (/light/.test(type)) return base + dexterityModifier;
    if (/medium/.test(type)) return base + Math.min(2, dexterityModifier);
    return base;
  }).filter((value) => value !== null);

  const setArmorClass = modifiers
    .filter((modifier) => (
      isGrantedModifier(modifier)
      && normalizeKey(modifier.type) === 'set'
      && /armor-class|unarmored-armor-class/.test(getModifierSubtype(modifier))
    ))
    .map((modifier) => toNumber(modifier.value))
    .filter((value) => value !== null);
  const globalBonus = modifiers
    .filter((modifier) => (
      isGrantedModifier(modifier)
      && normalizeKey(modifier.type) === 'bonus'
      && /(^|-)armor-class$/.test(getModifierSubtype(modifier))
    ))
    .reduce((sum, modifier) => sum + (toNumber(modifier.value) ?? 0), 0);
  const baseCandidates = [10 + dexterityModifier, ...armorCandidates, ...setArmorClass];
  return {
    value: Math.max(...baseCandidates) + shieldBonus + globalBonus,
    notes: bodyArmor[0]?.name || (setArmorClass.length ? 'D&D Beyond armor formula' : 'Unarmored')
  };
};

const buildHitPoints = (character, classes, abilities, modifiers) => {
  const totalLevel = classes.reduce((sum, entry) => sum + entry.level, 0);
  const override = toNumber(character.overrideHitPoints);
  const base = toNumber(character.baseHitPoints) ?? 0;
  const manualBonus = toNumber(character.bonusHitPoints) ?? 0;
  const fixedBonus = modifiers
    .filter((modifier) => (
      isGrantedModifier(modifier)
      && normalizeKey(modifier.type) === 'bonus'
      && getModifierSubtype(modifier) === 'hit-points'
    ))
    .reduce((sum, modifier) => sum + (toNumber(modifier.value) ?? 0), 0);
  const perLevelBonus = modifiers
    .filter((modifier) => (
      isGrantedModifier(modifier)
      && normalizeKey(modifier.type) === 'bonus'
      && getModifierSubtype(modifier) === 'hit-points-per-level'
    ))
    .reduce((sum, modifier) => sum + (toNumber(modifier.value) ?? 0), 0) * totalLevel;
  const calculatedMaximum = base
    + manualBonus
    + abilityModifier(abilities.constitution) * totalLevel
    + fixedBonus
    + perLevelBonus;
  const maximum = override ?? Math.max(1, calculatedMaximum);
  const removed = Math.max(0, toNumber(character.removedHitPoints) ?? 0);
  return {
    hp: Math.max(0, maximum - removed),
    maxHp: maximum,
    tempHp: Math.max(0, toNumber(character.temporaryHitPoints) ?? 0)
  };
};

const mapFeature = (rawFeature, source, sourceSection, level = null) => {
  const definition = isObject(rawFeature?.definition) ? rawFeature.definition : rawFeature;
  if (!isObject(definition)) return null;
  const name = String(definition.name || rawFeature?.name || '').trim();
  if (!name) return null;
  return {
    name,
    description: String(definition.description || definition.snippet || definition.summary || '').trim(),
    source,
    ...(level !== null ? { level } : {}),
    parserMeta: parserMeta(sourceSection)
  };
};

const buildFeatures = (character, classes) => {
  const classFeatures = [];
  classes.forEach((normalizedClass) => {
    const originalClass = (Array.isArray(character.classes) ? character.classes : []).find((entry) => (
      normalizeKey(entry?.definition?.name || entry?.name) === normalizeKey(normalizedClass.name)
    ));
    const baseFeatures = originalClass?.definition?.classFeatures || [];
    baseFeatures.forEach((feature) => {
      const requiredLevel = toInteger(feature?.requiredLevel ?? feature?.definition?.requiredLevel) ?? 1;
      if (requiredLevel > normalizedClass.level) return;
      const mapped = mapFeature(feature, normalizedClass.name, 'classFeatures', requiredLevel);
      if (mapped) classFeatures.push(mapped);
    });
    const subclassName = normalizedClass.subclass;
    const subclassFeatures = originalClass?.subclassDefinition?.classFeatures || [];
    subclassFeatures.forEach((feature) => {
      const requiredLevel = toInteger(feature?.requiredLevel ?? feature?.definition?.requiredLevel) ?? 1;
      if (requiredLevel > normalizedClass.level) return;
      const mapped = mapFeature(feature, subclassName || normalizedClass.name, 'classFeatures', requiredLevel);
      if (mapped) classFeatures.push(mapped);
    });
  });

  const speciesName = character?.race?.fullName || character?.race?.baseRaceName || character?.race?.baseName || 'Species';
  const speciesTraits = (Array.isArray(character?.race?.racialTraits) ? character.race.racialTraits : [])
    .map((trait) => mapFeature(trait, speciesName, 'speciesTraits'))
    .filter(Boolean);
  const feats = (Array.isArray(character.feats) ? character.feats : [])
    .map((feat) => mapFeature(feat, feat?.definition?.source || 'Feat', 'feats'))
    .filter(Boolean);
  const features = [];
  const backgroundDefinition = character?.background?.definition;
  if (backgroundDefinition?.featureName) {
    const backgroundFeature = mapFeature({
      name: backgroundDefinition.featureName,
      description: backgroundDefinition.featureDescription
    }, backgroundDefinition.name || 'Background', 'features');
    if (backgroundFeature) {
      if (backgroundDefinition.featureIsFeat) feats.push(backgroundFeature);
      else features.push(backgroundFeature);
    }
  }

  const optionBuckets = isObject(character.options) ? character.options : {};
  Object.entries(optionBuckets).forEach(([bucket, entries]) => {
    (Array.isArray(entries) ? entries : []).forEach((option) => {
      const target = bucket === 'race' ? speciesTraits : (bucket === 'feat' ? feats : classFeatures);
      const mapped = mapFeature(option, titleCase(bucket), bucket === 'race' ? 'speciesTraits' : (bucket === 'feat' ? 'feats' : 'classFeatures'));
      if (mapped) target.push(mapped);
    });
  });

  return {
    features: dedupeEntries(features),
    classFeatures: dedupeEntries(classFeatures),
    speciesTraits: dedupeEntries(speciesTraits),
    feats: dedupeEntries(feats)
  };
};

const mapUses = (limitedUse) => {
  if (!isObject(limitedUse)) return undefined;
  const max = toNumber(limitedUse.maxUses ?? limitedUse.max ?? limitedUse.numberOfUses);
  const used = toNumber(limitedUse.numberUsed ?? limitedUse.used) ?? 0;
  const reset = limitedUse.resetType || limitedUse.resetTypeDescription || limitedUse.reset || '';
  if (max === null && !reset) return undefined;
  return {
    ...(max !== null ? { max, current: Math.max(0, max - used) } : {}),
    ...(reset ? { reset: String(reset) } : {})
  };
};

const getActivationTypeName = (value) => {
  const numeric = toInteger(value);
  if (numeric !== null) {
    return {
      1: 'Action',
      2: 'No Action',
      3: 'Bonus Action',
      4: 'Reaction',
      5: 'Minute',
      6: 'Hour',
      7: 'Special',
      8: 'Legendary Action',
      9: 'Lair Action'
    }[numeric] || 'Action';
  }
  return String(value || 'Action').trim();
};

const mapAction = (action) => {
  if (!isObject(action) || !action.name) return null;
  const activationType = getActivationTypeName(action.activation?.activationType ?? action.activationType);
  const saveAbility = ABILITY_BY_ID[toInteger(action.saveStatId)]?.name;
  const saveDc = toNumber(action.fixedSaveDc);
  const uses = mapUses(action.limitedUse);
  return {
    name: action.name,
    description: String(action.description || action.snippet || '').trim(),
    source: action.source || titleCase(action._ddbBucket),
    activationType,
    actionType: normalizeKey(activationType),
    activationTime: toNumber(action.activation?.activationTime) ?? 1,
    ...(uses ? { uses } : {}),
    ...(saveAbility || saveDc !== null
      ? { save: { ...(saveAbility ? { ability: saveAbility } : {}), ...(saveDc !== null ? { dc: saveDc } : {}) } }
      : {}),
    ...(action.range ? { range: action.range } : {}),
    parserMeta: parserMeta('actions')
  };
};

const buildActions = (character) => {
  const buckets = { actions: [], bonusActions: [], reactions: [] };
  flattenBuckets(character.actions).forEach((rawAction) => {
    const mapped = mapAction(rawAction);
    if (!mapped) return;
    const activation = normalizeKey(mapped.activationType);
    if (activation === 'reaction') buckets.reactions.push(mapped);
    else if (activation === 'bonus-action') buckets.bonusActions.push(mapped);
    else buckets.actions.push(mapped);
  });
  return {
    actions: dedupeEntries(buckets.actions),
    bonusActions: dedupeEntries(buckets.bonusActions),
    reactions: dedupeEntries(buckets.reactions)
  };
};

const mapSpell = (spell, source) => {
  const definition = isObject(spell?.definition) ? spell.definition : spell;
  if (!isObject(definition) || !definition.name) return null;
  const level = toInteger(definition.level) ?? 0;
  const school = getNamedValue(definition.school);
  const activation = definition.activation;
  const components = Array.isArray(definition.components)
    ? definition.components.map((component) => ({ 1: 'V', 2: 'S', 3: 'M' }[component] || component)).join(', ')
    : definition.components;
  return {
    name: definition.name,
    level,
    ...(school ? { school } : {}),
    ...(activation ? {
      castingTime: `${activation.activationTime ?? 1} ${getActivationTypeName(activation.activationType).toLowerCase()}`
    } : {}),
    ...(definition.range ? { range: definition.range } : {}),
    ...(definition.duration ? { duration: definition.duration } : {}),
    ...(components ? { components } : {}),
    ...(definition.componentsDescription ? { material: definition.componentsDescription } : {}),
    description: String(definition.description || definition.snippet || '').trim(),
    concentration: definition.concentration === true,
    ritual: definition.ritual === true,
    prepared: spell?.prepared === true,
    known: spell?.countsAsKnownSpell === true,
    alwaysPrepared: spell?.alwaysPrepared === true,
    usesSpellSlot: spell?.usesSpellSlot !== false,
    source: spell?.source || source || '',
    sourceId: getStableSourceId(definition.id, spell?.id) ?? null,
    parserMeta: parserMeta('spells')
  };
};

const buildSpells = (character, classes, abilities, proficiencyBonus) => {
  const allSpells = [];
  const classById = new Map();
  (Array.isArray(character.classes) ? character.classes : []).forEach((entry) => {
    const name = entry?.definition?.name || entry?.name;
    [entry?.id, entry?.definitionId, entry?.definition?.id].forEach((id) => {
      if (id !== null && id !== undefined && name) classById.set(String(id), name);
    });
  });
  (Array.isArray(character.classSpells) ? character.classSpells : []).forEach((classSpellGroup) => {
    const source = classById.get(String(classSpellGroup?.characterClassId)) || 'Class';
    (Array.isArray(classSpellGroup?.spells) ? classSpellGroup.spells : []).forEach((spell) => {
      const mapped = mapSpell(spell, source);
      if (mapped) allSpells.push(mapped);
    });
  });
  flattenBuckets(character.spells).forEach((spell) => {
    const mapped = mapSpell(spell, titleCase(spell._ddbBucket));
    if (mapped) allSpells.push(mapped);
  });
  const uniqueSpells = dedupeEntries(allSpells);
  const byLevel = {};
  uniqueSpells.forEach((spell) => {
    const level = String(spell.level ?? 0);
    if (!byLevel[level]) byLevel[level] = [];
    byLevel[level].push(spell);
  });

  const slots = {};
  (Array.isArray(character.spellSlots) ? character.spellSlots : []).forEach((slot) => {
    const level = toInteger(slot?.level);
    const total = toNumber(slot?.available);
    if (level === null || total === null || total <= 0) return;
    slots[String(level)] = { total, used: Math.max(0, toNumber(slot?.used) ?? 0) };
  });
  if (isObject(character.pactMagic)) {
    const level = toInteger(character.pactMagic.level);
    const total = toNumber(character.pactMagic.available);
    if (level !== null && total !== null && total > 0) {
      slots[String(level)] = {
        total: (slots[String(level)]?.total || 0) + total,
        used: (slots[String(level)]?.used || 0) + Math.max(0, toNumber(character.pactMagic.used) ?? 0),
        pact: true
      };
    }
  }

  const casterClasses = (Array.isArray(character.classes) ? character.classes : []).filter((entry) => (
    toInteger(entry?.definition?.spellCastingAbilityId) !== null
  ));
  const primaryCaster = casterClasses[0];
  const spellAbility = ABILITY_BY_ID[toInteger(primaryCaster?.definition?.spellCastingAbilityId)]?.name;
  const abilityBonus = spellAbility ? abilityModifier(abilities[spellAbility]) : 0;
  const saveDC = toNumber(character.spellSaveDcOverride) ?? (spellAbility ? 8 + proficiencyBonus + abilityBonus : null);
  const attackBonus = toNumber(character.spellAttackBonusOverride) ?? (spellAbility ? proficiencyBonus + abilityBonus : null);
  const spellcastingClasses = casterClasses.map((entry) => {
    const ability = ABILITY_BY_ID[toInteger(entry?.definition?.spellCastingAbilityId)]?.name;
    return {
      name: entry?.definition?.name || 'Class',
      ability,
      level: toInteger(entry?.level) ?? 0
    };
  });

  return {
    spells: {
      spells: uniqueSpells,
      known: uniqueSpells.filter((spell) => spell.known),
      prepared: uniqueSpells.filter((spell) => spell.prepared || spell.alwaysPrepared),
      atWill: uniqueSpells.filter((spell) => spell.level === 0 || spell.usesSpellSlot === false),
      byLevel,
      slots,
      alwaysPrepared: Object.fromEntries(
        uniqueSpells.filter((spell) => spell.alwaysPrepared).map((spell) => [spell.name, true])
      )
    },
    ...(spellAbility ? {
      spellcasting: {
        ability: spellAbility,
        ...(saveDC !== null ? { saveDC } : {}),
        ...(attackBonus !== null ? { attackBonus } : {}),
        classes: spellcastingClasses
      }
    } : {})
  };
};

const buildDefensesAndSenses = (character, modifiers) => {
  const resistances = [...(Array.isArray(character.damageResistances) ? character.damageResistances : [])];
  const immunities = [];
  const vulnerabilities = [];
  const conditionImmunities = [];
  const senses = {};
  modifiers.filter(isGrantedModifier).forEach((modifier) => {
    const type = normalizeKey(modifier.type);
    const subtype = getModifierSubtype(modifier);
    const label = titleCase(modifier.friendlySubtypeName || modifier.subType);
    const coreSubtype = subtype.replace(/-damage$/, '').replace(/-condition$/, '');
    if (type === 'resistance') resistances.push(titleCase(coreSubtype));
    if (type === 'vulnerability') vulnerabilities.push(titleCase(coreSubtype));
    if (type === 'immunity') {
      if (CONDITION_NAMES.has(coreSubtype)) conditionImmunities.push(titleCase(coreSubtype));
      else if (DAMAGE_TYPES.has(coreSubtype)) immunities.push(titleCase(coreSubtype));
      else if (label) conditionImmunities.push(label);
    }
    if (
      ['sense', 'set', 'darkvision', 'blindsight', 'tremorsense', 'truesight'].includes(type)
      && ['darkvision', 'blindsight', 'tremorsense', 'truesight'].includes(subtype)
    ) {
      const value = toNumber(modifier.value);
      if (value !== null) senses[subtype] = Math.max(senses[subtype] || 0, value);
    }
  });
  return {
    resistances: dedupeStrings(resistances),
    immunities: dedupeStrings(immunities),
    vulnerabilities: dedupeStrings(vulnerabilities),
    conditionImmunities: dedupeStrings(conditionImmunities),
    senses
  };
};

const buildBiography = (character) => {
  const traits = isObject(character.traits) ? character.traits : {};
  const notes = isObject(character.notes) ? character.notes : {};
  return {
    personality: String(traits.personalityTraits || '').trim(),
    ideals: String(traits.ideals || '').trim(),
    bonds: String(traits.bonds || '').trim(),
    flaws: String(traits.flaws || '').trim(),
    appearance: String(traits.appearance || '').trim(),
    backstory: String(notes.backstory || '').trim(),
    allies: String(notes.allies || '').trim(),
    goals: String(notes.organizations || '').trim(),
    notes: dedupeStrings([
      notes.otherNotes,
      notes.personalPossessions,
      notes.otherHoldings,
      notes.enemies ? `Enemies: ${notes.enemies}` : ''
    ]).join('\n'),
    age: character.age || '',
    height: character.height || '',
    weight: character.weight || '',
    eyes: character.eyes || '',
    hair: character.hair || '',
    skin: character.skin || '',
    gender: character.gender || '',
    faith: character.faith || ''
  };
};

export const isDndBeyondCharacterData = (value) => Boolean(
  isObject(value)
  && typeof value.name === 'string'
  && Array.isArray(value.classes)
  && value.classes.some((entry) => isObject(entry?.definition) && typeof entry.definition.name === 'string')
  && Array.isArray(value.stats)
  && value.stats.some((entry) => ABILITY_BY_ID[Number(entry?.id)])
  && (isObject(value.race) || Array.isArray(value.inventory) || isObject(value.modifiers))
);

export const getDndBeyondCharacterData = (value) => {
  if (isDndBeyondCharacterData(value)) return value;
  if (isObject(value) && isDndBeyondCharacterData(value.data)) return value.data;
  return null;
};

export const mapDndBeyondCharacter = (value, warnings = []) => {
  const character = getDndBeyondCharacterData(value);
  if (!character) return null;

  const modifiers = flattenBuckets(character.modifiers);
  const classes = buildClasses(character);
  const totalLevel = classes.reduce((sum, entry) => sum + entry.level, 0);
  const primaryClass = classes[0] || {};
  const abilities = buildAbilities(character, modifiers);
  const proficiencyBonus = toNumber(character.proficiencyBonusOverride)
    ?? Math.floor((Math.max(1, totalLevel) - 1) / 4) + 2;
  const training = buildTraining(character, modifiers, abilities, proficiencyBonus);
  const equipment = buildEquipment(character);
  const hp = buildHitPoints(character, classes, abilities, modifiers);
  const speed = buildSpeed(character, modifiers);
  const ac = buildArmorClass(character, equipment, abilities, modifiers);
  const features = buildFeatures(character, classes);
  const actions = buildActions(character);
  const spellData = buildSpells(character, classes, abilities, proficiencyBonus);
  const defenses = buildDefensesAndSenses(character, modifiers);
  const biography = buildBiography(character);
  const initiative = toNumber(character.initiativeOverride)
    ?? abilityModifier(abilities.dexterity)
      + modifiers
        .filter((modifier) => (
          isGrantedModifier(modifier)
          && normalizeKey(modifier.type) === 'bonus'
          && getModifierSubtype(modifier) === 'initiative'
        ))
        .reduce((sum, modifier) => sum + (toNumber(modifier.value) ?? 0), 0);
  const perception = training.skillsDetailed.find((skill) => normalizeKey(skill.name) === 'perception');
  const passivePerception = toNumber(character.passivePerceptionOverride)
    ?? 10 + (perception?.bonus ?? abilityModifier(abilities.wisdom));
  const race = String(
    character?.race?.fullName
    || character?.race?.baseRaceName
    || character?.race?.baseName
    || ''
  ).trim();
  const background = String(
    character?.background?.definition?.name
    || character?.background?.customBackground?.name
    || ''
  ).trim();
  const hitDice = (Array.isArray(character.hitDice) && character.hitDice.length
    ? character.hitDice.map((entry) => {
        const total = toInteger(entry?.total) ?? 0;
        const die = toInteger(entry?.dieSize);
        return total > 0 && die ? `${total}d${die}` : '';
      })
    : classes.map((entry) => entry.hitDice || '')
  ).filter(Boolean).join('+');

  if (!classes.length) warnings.push('No class progression was found in the D&D Beyond character export.');
  if (!race) warnings.push('No species was found in the D&D Beyond character export.');

  return {
    sourceFormat: DND_BEYOND_SOURCE_FORMAT,
    sourceCharacterId: character.id ?? null,
    completeCharacterImport: true,
    name: character.name,
    class: primaryClass.name || '',
    subclass: primaryClass.subclass || '',
    classes,
    level: totalLevel || 1,
    experience: toNumber(character.currentXp) ?? 0,
    race,
    species: race,
    background,
    alignment: ALIGNMENTS[toInteger(character.alignmentId)] || '',
    size: character?.race?.size || 'Medium',
    inspiration: character.inspiration === true,
    proficiencyBonus,
    initiative,
    passivePerception,
    speed,
    ac,
    ...hp,
    hitDice,
    hitDiceSpent: (Array.isArray(character.hitDice) ? character.hitDice : [])
      .reduce((sum, entry) => sum + (toInteger(entry?.used) ?? 0), 0),
    deathSaves: {
      successes: toInteger(character?.deathSaves?.successCount) ?? 0,
      failures: toInteger(character?.deathSaves?.failCount) ?? 0
    },
    abilities,
    ...training,
    equipment,
    inventory: {
      equipment: [
        ...(equipment.weapons || []),
        ...(equipment.armor || []),
        ...(equipment.items || []),
        ...(equipment.valuables || [])
      ],
      currency: { ...(equipment.currency || {}) }
    },
    ...features,
    ...actions,
    ...spellData,
    ...defenses,
    biography,
    allies: biography.allies,
    appearance: biography.appearance,
    notes: biography.notes,
    artwork: character?.decorations?.avatarUrl || character?.avatarUrl || null,
    conditions: Array.isArray(character.conditions)
      ? character.conditions.map((condition) => condition?.name || condition).filter(Boolean)
      : []
  };
};

export default mapDndBeyondCharacter;
